/**
 * キャリブレーションユーティリティ
 *
 * 画面のピクセル/mm変換を管理
 */

const STORAGE_KEY = 'device_settings';

/**
 * デバイスキャリブレーション設定
 */
export interface CalibrationData {
  /** 画面の幅（mm） */
  screenWidthMm: number;
  /** 画面の高さ（mm） */
  screenHeightMm: number;
  /** Pixels per mm */
  ppmm: number;
  /** キャリブレーション日時 */
  calibratedAt: string;
  method?: 'line' | 'card';
}

/**
 * デフォルトのキャリブレーション値
 * 一般的なタブレット（iPad 10.2インチ）を想定
 * 実際の使用時はキャリブレーションを推奨
 */
const DEFAULT_CALIBRATION: CalibrationData = {
  screenWidthMm: 218, // 約218mm (10.2インチの横)
  screenHeightMm: 164, // 約164mm (10.2インチの縦)
  ppmm: window.innerWidth / 218, // 画面幅から推定
  calibratedAt: '',
};

/**
 * キャリブレーションデータを取得
 *
 * @returns キャリブレーションデータ（未設定の場合はデフォルト値）
 */
export function getCalibration(): CalibrationData {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      if (data.ppmm && data.screenWidthMm && data.screenHeightMm) {
        return {
          screenWidthMm: data.screenWidthMm,
          screenHeightMm: data.screenHeightMm,
          ppmm: data.ppmm,
          calibratedAt: data.calibratedAt || '',
        };
      }
    }
  } catch {
    console.warn('キャリブレーションデータの読み込みに失敗しました');
  }

  // デフォルト値を返す（画面サイズから推定）
  return {
    ...DEFAULT_CALIBRATION,
    ppmm: window.innerWidth / DEFAULT_CALIBRATION.screenWidthMm,
  };
}

/**
 * キャリブレーションデータを保存
 *
 * @param data - キャリブレーションデータ
 */
export function saveCalibration(data: CalibrationData): void {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    const existingData = existing ? JSON.parse(existing) : {};

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...existingData,
        ...data,
        calibratedAt: new Date().toISOString(),
      })
    );
  } catch {
    console.error('キャリブレーションデータの保存に失敗しました');
  }
}

/**
 * キャリブレーションが実施済みかチェック
 *
 * @returns キャリブレーション済みかどうか
 */
export function isCalibrated(): boolean {
  const calibration = getCalibration();
  return !!calibration.calibratedAt;
}

/**
 * mm から px に変換
 *
 * @param mm - ミリメートル値
 * @param ppmm - Pixels per mm（省略時は保存値を使用）
 * @returns ピクセル値
 */
export function mmToPx(mm: number, ppmm?: number): number {
  const actualPpmm = ppmm ?? getCalibration().ppmm;
  return mm * actualPpmm;
}

/**
 * px から mm に変換
 *
 * @param px - ピクセル値
 * @param ppmm - Pixels per mm（省略時は保存値を使用）
 * @returns ミリメートル値
 */
export function pxToMm(px: number, ppmm?: number): number {
  const actualPpmm = ppmm ?? getCalibration().ppmm;
  return px / actualPpmm;
}

/**
 * Canvas寸法をmm単位で取得
 *
 * @param canvasWidthPx - Canvas幅（px）
 * @param canvasHeightPx - Canvas高さ（px）
 * @returns Canvas寸法（mm）
 */
export function getCanvasDimensionsMm(
  canvasWidthPx: number,
  canvasHeightPx: number
): { widthMm: number; heightMm: number } {
  const calibration = getCalibration();
  return {
    widthMm: canvasWidthPx / calibration.ppmm,
    heightMm: canvasHeightPx / calibration.ppmm,
  };
}

/**
 * 既知の長さから ppmm を計算
 * キャリブレーション時に使用
 *
 * @param knownLengthMm - 既知の長さ（mm）
 * @param measuredLengthPx - 画面上で測定した長さ（px）
 * @returns Pixels per mm
 */
export function calculatePpmm(
  knownLengthMm: number,
  measuredLengthPx: number
): number {
  return measuredLengthPx / knownLengthMm;
}

/**
 * 画面サイズから推定されたキャリブレーション値を生成
 * 実際のキャリブレーションができない場合の代替
 *
 * @param screenDiagonalInch - 画面の対角サイズ（インチ）
 * @returns 推定キャリブレーションデータ
 */
export function estimateCalibration(
  screenDiagonalInch: number
): CalibrationData {
  const screenWidthPx = window.innerWidth;
  const screenHeightPx = window.innerHeight;

  // 対角線のピクセル数
  const diagonalPx = Math.sqrt(
    screenWidthPx * screenWidthPx + screenHeightPx * screenHeightPx
  );

  // 対角線のmm（1インチ = 25.4mm）
  const diagonalMm = screenDiagonalInch * 25.4;

  // ppmm を計算
  const ppmm = diagonalPx / diagonalMm;

  return {
    screenWidthMm: screenWidthPx / ppmm,
    screenHeightMm: screenHeightPx / ppmm,
    ppmm,
    calibratedAt: '', // 推定値なのでキャリブレーション日時は空
  };
}
